<template>
  <div class="main-container">
    <h1>{{ $t("externalUsers.title") }}</h1>
    <div class="group-button-table d-flex flex-wrap align-items-center coefficient-toolbar mt-2 justify-content-between">
      <div class="d-flex">
        <button
          type="button"
          class="btn btn-secondary mr-2 mb-1 d-flex align-items-center"
          @click="add"
        >
          <b-icon icon="plus" class="mr-1" scale="1.5" aria-hidden="true" variant="danger" />
          {{ $t('externalUsers.add') }}
        </button>
      </div>
      <div class="d-flex">
        <button
          type="button"
          class="btn btn-primary mr-2 mb-1 d-flex align-items-center"
          :disabled="!hasModifications"
          @click="save"
        >
          {{ $t('externalUsers.save') }}
        </button>
      </div>
    </div>
    <DataTable
      ref="table"
      :columns="dtColumns"
      :url="url"
      trackBy="id"
      :searchable="true"
      :searchLabel="$t('externalUsers.search').toString()"
      :parseResponse="parseResponse"
      :rowClass="rowClassFn"
    >
      <!-- Login -->
      <template #editable-login="{ row }">
        <input
          class="form-control form-control-sm"
          type="text"
          v-model="row.login"
          @input="markModified(row)"
        />
      </template>
      <!-- First name -->
      <template #editable-firstName="{ row }">
        <input
          class="form-control form-control-sm"
          type="text"
          v-model="row.firstName"
          @input="markModified(row)"
        />
      </template>
      <!-- Last name -->
      <template #editable-lastName="{ row }">
        <input
          class="form-control form-control-sm"
          type="text"
          v-model="row.lastName"
          @input="markModified(row)"
        />
      </template>
      <!-- Entreprise dropdown -->
      <template #dropdown-entreprise="{ row }">
        <div class="custom-actions">
          <div
            v-if="!editingEntreprise[row.id]"
            @click="beginEditEntreprise(row)"
          >
            {{ displayEntrepriseText(row) }}
          </div>
          <b-form-select
            v-else
            :ref="'entSel-'+row.id"
            class="form-control form-control-sm"
            v-model="row.idEntreprise"
            :options="entrepriseOptions"
            @change="handleEntrepriseSelectChange(row)"
            @blur="endEditEntreprise(row)"
          />
        </div>
      </template>
      <!-- Email -->
      <template #editable-mail="{ row }">
        <input
          class="form-control form-control-sm"
          type="email"
          v-model="row.mail"
          @input="markModified(row)"
        />
      </template>
      <!-- Contact switch -->
      <template #switch-isContact="{ row }">
        <button
          type="button"
          class="btn btn-switch toggle-switch"
          :class="row.isContact ? 'active-last' : 'active-first'"
          @click="toggleBoolean(row, 'isContact')"
        >
          <span class="switch-toggler" />
        </button>
      </template>
      <!-- Role dropdown -->
      <template #dropdown-role="{ row }">
        <div class="custom-actions">
          <div
            v-if="!editingRole[row.id]"
            @click="beginEditRole(row)"
          >
            {{ displayRoleText(row) }}
          </div>
          <b-form-select
            v-else
            :ref="'roleSel-'+row.id"
            class="form-control form-control-sm"
            v-model="row.idRole"
            :options="roleOptions"
            @change="handleRoleSelectChange(row)"
            @blur="endEditRole(row)"
          />
        </div>
      </template>
      <!-- Enabled switch -->
      <template #switch-isEnabled="{ row }">
        <button
          type="button"
          class="btn btn-switch toggle-switch"
          :class="row.isEnabled ? 'active-last' : 'active-first'"
          @click="toggleBoolean(row, 'isEnabled')"
        >
          <span class="switch-toggler" />
        </button>
      </template>
    </DataTable>
  </div>
</template>

<script lang="ts">
import ManageUser from "@/models/ManageUser";
import EntreprisesServices from "@/services/EntreprisesServices";
import UserRoleService from "@/services/UserRoleService";
import UserService from "@/services/UserService";
import Constants from "@/utils/Constants";
import { UserProfilEnum } from "@/utils/Enums";
import { Component,Vue,Watch } from "vue-property-decorator";

type Data = {
  columns: Array<Object>;
  rows: Array<Object>;
};

@Component({
  components: {
    DataTable: () => import("@/components/Shared/Table/DataTable.vue"),
  },
})
export default class ExternalUsers extends Vue {
  public data: Data = {
    columns: [],
    rows: [],
  };
  private modifiedUsers: Array<ManageUser> = [];
  private url: string = UserService.getTableUrl(
    "externalUser",
    this.$i18n.locale
  );
  private roles: Array<any> = [];
  private entreprises: Array<any> = [];
  private selectedRow: string[] = [];
  private editingEntreprise: Record<string, boolean> = {};
  private editingRole: Record<string, boolean> = {};
  // Options for selects
  private get roleOptions() {
    return this.roles.map((r: any) => ({ value: r.role.id, text: r.translation?.name || r.role?.name || '' }));
  }
  private get entrepriseOptions() {
    return this.entreprises.map((e: any) => ({ value: e.entreprise.id, text: e.translation?.name || e.entreprise?.name || '' }));
  }

  private get dtColumns(): Array<{ key: string; label: string; slot?: string; sortField?: string; sortable?: boolean }> {
    return [
      { key: 'login', label: this.$t('externalUsers.login').toString(), sortField: 'login', sortable: true, slot: 'editable-login' },
      { key: 'firstName', label: this.$t('externalUsers.firstName').toString(), sortField: 'firstName', sortable: true, slot: 'editable-firstName' },
      { key: 'lastName', label: this.$t('externalUsers.lastName').toString(), sortField: 'lastName', sortable: true, slot: 'editable-lastName' },
      { key: 'nameEntreprise', label: this.$t('externalUsers.entreprise').toString(), sortField: 'nameEntreprise', sortable: true, slot: 'dropdown-entreprise' },
      { key: 'mail', label: this.$t('externalUsers.email').toString(), sortField: 'mail', sortable: true, slot: 'editable-mail' },
      { key: 'isContact', label: this.$t('externalUsers.contact').toString(), sortField: 'isContact', sortable: true, slot: 'switch-isContact' },
      { key: 'nameRole', label: this.$t('externalUsers.role').toString(), sortField: 'nameRole', sortable: true, slot: 'dropdown-role' },
      { key: 'isEnabled', label: this.$t('externalUsers.enabled').toString(), sortField: 'isEnabled', sortable: true, slot: 'switch-isEnabled' },
    ];
  }

  public async save() {
    for (let i = 0; i < this.modifiedUsers.length; i++) {
      if (this.modifiedUsers[i].firstName == "") {
        this.sendErrorNotification(
          this.$t("externalUsers.updateFailed").toString(),
          this.$t("externalUsers.propertyFirstNameEmpty").toString()
        );
        return;
      } else if (this.modifiedUsers[i].lastName == "") {
        this.sendErrorNotification(
          this.$t("externalUsers.updateFailed").toString(),
          this.$t("externalUsers.propertyLastNameEmpty").toString()
        );
        return;
      } else if (this.modifiedUsers[i].login == "" ||/\s/.test(this.modifiedUsers[i].login)) {
        this.sendErrorNotification(
          this.$t("externalUsers.updateFailed").toString(),
          this.$t("externalUsers.propertyLoginValidationError").toString()
        );
        return;
      } else if (this.modifiedUsers[i].mail == "") {
        this.sendErrorNotification(
          this.$t("externalUsers.updateFailed").toString(),
          this.$t("externalUsers.propertyMailEmpty").toString()
        );
        return;
      }
    }
    try {
      await UserService.saveUsers(this.modifiedUsers, this.$i18n.locale);
      this.refresh();
      this.$store.dispatch('update');
      this.$notify({
        group: "global",
        type: "success",
        title: this.$t("externalUsers.updateSuccess.title").toString(),
        text: this.$t("externalUsers.updateSuccess.description").toString(),
      });
    } catch (err: any) {
      let errMessage = this.$t("externalUsers.uknownError").toString();
      if (err?.response?.status === 400 || err?.response?.status === 500) {
        switch (err?.response?.data?.errorCode) {
          case "duplicateUserLogin":
            errMessage = this.$t("externalUsers.duplicateUserLogin").toString();
            break;
          case "duplicateUserMail":
            errMessage = this.$t("externalUsers.duplicateUserMail").toString();
            break;
          case "duplicateUserIdSap":
            errMessage = this.$t("externalUsers.duplicateUserIdSap").toString();
            break;
          case "notEnoughPermissions":
            errMessage = this.$t("externalUsers.notEnoughPermissions").toString();
            break;
          default:
            errMessage = this.$t(`externalUsers.${err?.response?.data?.errorCode}`).toString();
        }
      }
      this.sendErrorNotification(
        this.$t("externalUsers.updateFailed").toString(),
        errMessage
      );
    }
  }
  private refresh() {
    this.modifiedUsers = [];
    const api: any = (this.$refs.table as any)?.api;
    if (api && this.url) api.load(this.url);
  }
  private sendErrorNotification(title: string, text: string) {
    this.$notify({
      group: "globalError",
      type: "error",
      title: title,
      text: text,
      duration: Constants.DEFAULT_WAIT_ERROR_NOTIFICATIONS,
    });
  }

  public add(): void {
    const newUser = new ManageUser();
    newUser.keyProfil = "externalUser";
    const api: any = (this.$refs.table as any)?.api;
    if (api && api.rows) {
      api.rows.unshift(newUser);
    }
    this.markModified(newUser as any);
    if (newUser.id && !this.selectedRow.includes(newUser.id)) this.selectedRow.push(newUser.id);
  }
  
  private async loadElements() {
    this.roles = (
      await UserRoleService.getRolesByProfil(
        this.$i18n.locale,
        UserProfilEnum.externalUser
      )
    ).data;
    this.entreprises = (
      await EntreprisesServices.getEntreprises(this.$i18n.locale)
    ).data.filter(entr => entr.entreprise.isEnabled);
  }
  public mounted() {
    
    this.loadElements();
 
   
  }
  @Watch("$i18n.locale")
  private onLocaleChange() {
    this.loadElements();
    this.url = UserService.getTableUrl("externalUser", this.$i18n.locale);
  }

  private parseResponse(resp: any): { rows: any[]; total: number } {
    const d = resp?.data;
    if (!d) return { rows: [], total: 0 };
    const rows: any[] = Array.isArray(d.data) ? d.data : (Array.isArray(d.rows) ? d.rows : (Array.isArray(d) ? d : []));
    const total = d.pagination?.total || d.totalCount || rows.length;
    this.data.rows = rows;
    return { rows, total };
  }

  get hasModifications(): boolean {
    return this.modifiedUsers.length > 0;
  }

  private markModified(row: any) {
    const id = row.id || row.login || row.mail;
    const idx = this.modifiedUsers.findIndex((x: any) => (x.id || x.login || x.mail) === id);
    if (idx === -1) this.modifiedUsers.push(row);
    else this.modifiedUsers.splice(idx, 1, row);
  }

  private rowClassFn(row: any) {
    return this.selectedRow.includes(row.id) ? 'selected-row' : '';
  }

  private toggleBoolean(row: any, prop: 'isContact' | 'isEnabled') {
    row[prop] = !row[prop];
    this.markModified(row);
  }

  private onRoleChange(row: any) {
    const sel = this.roles.find((r: any) => r.role?.id === row.idRole);
    row.nameRole = sel?.translation?.name || sel?.role?.name || row.nameRole;
    if (sel?.role?.key) row.keyRole = sel.role.key;
    this.markModified(row);
  }
  private displayRoleText(row:any): string {
    const name = row.nameRole;
    if (name && name.length) return name;
    const sel = this.roles.find((r: any) => r.role?.id === row.idRole);
    return sel?.translation?.name || sel?.role?.name || '--';
  }
  private beginEditRole(row:any) {
    this.$set(this.editingRole, row.id, true);
    this.$nextTick(()=>{
      const el:any = (this.$refs as any)[`roleSel-${row.id}`];
      if (el?.focus) el.focus();
    });
  }
  private endEditRole(row:any) { this.$set(this.editingRole, row.id, false); }
  private handleRoleSelectChange(row:any) { this.onRoleChange(row); this.endEditRole(row); }

  private onEntrepriseChange(row: any) {
    const sel = this.entreprises.find((e: any) => e.entreprise?.id === row.idEntreprise);
    row.nameEntreprise = sel?.translation?.name || sel?.entreprise?.name || row.nameEntreprise;
    this.markModified(row);
  }
  private displayEntrepriseText(row:any): string {
    const name = row.nameEntreprise;
    if (name && name.length) return name;
    const sel = this.entreprises.find((e: any) => e.entreprise?.id === row.idEntreprise);
    return sel?.translation?.name || sel?.entreprise?.name || '--';
  }
  private beginEditEntreprise(row:any) {
    this.$set(this.editingEntreprise, row.id, true);
    this.$nextTick(()=>{
      const el:any = (this.$refs as any)[`entSel-${row.id}`];
      if (el?.focus) el.focus();
    });
  }
  private endEditEntreprise(row:any) { this.$set(this.editingEntreprise, row.id, false); }
  private handleEntrepriseSelectChange(row:any) { this.onEntrepriseChange(row); this.endEditEntreprise(row); }
}
</script>
<style scoped>
.dropdown-display {
  min-height: 28px;
  line-height: 28px;
  padding: 0 6px;
  border: 1px solid #ced4da;
  border-radius: 0.2rem;
  background-color: #fff;
  font-size: 0.875rem;
  cursor: pointer;
}
.dropdown-display.is-disabled {
  cursor: default;
  background-color: #e9ecef;
}
</style>
